import React, { useState, useEffect } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Contract, Task, DEFAULT_TRANSPORTATION_OPTIONS } from '../types/contract';
import './ContractEditor.css';

interface ContractEditorProps {
  contract: Contract;
  onSave: (contract: Contract) => void;
  onDelete: (contractId: string) => void;
  onBack: () => void;
}

const ContractEditor: React.FC<ContractEditorProps> = ({ 
  contract, 
  onSave, 
  onDelete, 
  onBack 
}) => {
  const [editedContract, setEditedContract] = useState<Contract>(contract);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setEditedContract(contract);
  }, [contract]);

  const handleContractFieldChange = (field: keyof Contract, value: any) => {
    setEditedContract(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  // Task editing is handled in MainDashboard

  const calculateProgress = (tasks: Task[]) => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(task => 
      task.status === 'Completed' || task.status === 'Confirmed' || task.status === 'Booked'
    ).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedContract = {
        ...editedContract,
        progress: calculateProgress(editedContract.tasks),
        updatedAt: new Date().toISOString()
      };

      // Let the parent component (App.tsx) handle Firestore operations
      // It will check if this is a temp ID and create/update accordingly
      onSave(updatedContract);
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Error saving contract. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contract.id) return;
    
    try {
      await deleteDoc(doc(db, 'contracts', contract.id));
      onDelete(contract.id);
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Error deleting contract. Please try again.');
    }
  };

  // Status color logic moved to MainDashboard

  return (
    <div className="contract-editor-container">
      <div className="editor-header">
        <div className="header-left">
          <button onClick={onBack} className="back-button">
            ← Back to Contracts
          </button>
          <h1>{editedContract.contractNumber}</h1>
        </div>
        <div className="header-right">
          <button 
            onClick={handleSave} 
            disabled={isSaving}
            className="save-button"
          >
            {isSaving ? 'Saving...' : 'Save Contract'}
          </button>
          <button 
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-button"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="editor-content">
        {/* For new contracts (temp ID), hide everything - just show header with Save/Delete */}
        {!contract.id.startsWith('temp_') && (
          <>
            {/* Contract Information section hidden - fields are set via prompts */}
            {/* <div className="contract-info-section">
              <h2>Contract Information</h2>
              <div className="info-grid">
                <div className="info-group">
                  <label>Contract Number</label>
                  <input
                    type="text"
                    value={editedContract.contractNumber}
                    onChange={(e) => handleContractFieldChange('contractNumber', e.target.value)}
                  />
                </div>
                <div className="info-group">
                  <label>Product</label>
                  <input
                    type="text"
                    value={editedContract.product}
                    onChange={(e) => handleContractFieldChange('product', e.target.value)}
                  />
                </div>
                <div className="info-group">
                  <label>Transportation Vehicle</label>
                  <select
                    value={editedContract.transportationVehicle}
                    onChange={(e) => handleContractFieldChange('transportationVehicle', e.target.value)}
                  >
                    {DEFAULT_TRANSPORTATION_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="info-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={editedContract.year}
                    onChange={(e) => handleContractFieldChange('year', parseInt(e.target.value))}
                  />
                </div>
                <div className="info-group">
                  <label>Status</label>
                  <select
                    value={editedContract.status}
                    onChange={(e) => handleContractFieldChange('status', e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="info-group">
                  <label>Progress</label>
                  <div className="progress-display">
                    <span>{calculateProgress(editedContract.tasks)}%</span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${calculateProgress(editedContract.tasks)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Tasks section removed - tasks are displayed in MainDashboard */}
          </>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="modal-content">
            <h3>Delete Contract</h3>
            <p>Are you sure you want to delete "{editedContract.contractNumber}"? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="confirm-delete-button"
              >
                Delete Contract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractEditor;





